/**
 * Client-side persistence (FR-64, D-8, D-9, NFR-9).
 *
 * Guest state remains browser-local: `localStorage` for small config and
 * IndexedDB for the multi-megabyte player cache. Optional account sync adds a
 * separate per-user namespace; clearing site data removes both local copies.
 *
 * The guest namespace is intentionally independent from account state so a
 * signed-in session can be discarded without destroying a prior guest setup.
 */

const DB_NAME = 'draftpuppy';
const DB_VERSION = 1;
const STORE = 'cache';
const LS_PREFIX = 'draftpuppy:';
const DRAFT_OVERRIDES_KEY = 'draft_overrides';

/** Account caches are deliberately namespaced so signing out can restore the
 * pre-existing guest profile without copying or deleting it. */
const accountKey = (userId: string, key: string): string => `account:${userId}:${key}`;

interface CacheEnvelope<T> {
  value: T;
  stored_at: number;
  /** Milliseconds; null means no expiry. */
  ttl_ms: number | null;
}

// ---------------------------------------------------------------------------
// IndexedDB — large objects (the ~14MB player map)
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    // A blocked or failed open is not fatal — the app degrades to refetching
    // the player map, which is slow but correct. Never let storage kill a draft.
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
}

export async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const envelope = req.result as CacheEnvelope<T> | undefined;
        if (!envelope) return resolve(null);
        if (isExpired(envelope)) return resolve(null);
        resolve(envelope.value);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function idbSet<T>(key: string, value: T, ttlMs: number | null = null): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const envelope: CacheEnvelope<T> = { value, stored_at: Date.now(), ttl_ms: ttlMs };
      tx.objectStore(STORE).put(envelope, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export async function idbMeta(key: string): Promise<{ stored_at: number; ttl_ms: number | null } | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const envelope = req.result as CacheEnvelope<unknown> | undefined;
        resolve(envelope ? { stored_at: envelope.stored_at, ttl_ms: envelope.ttl_ms } : null);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
  } catch {
    // Nothing to do — a failed delete just means a stale entry lingers.
  }
}

export function scopedLsGet<T>(userId: string, key: string, fallback: T): T {
  return lsGet(accountKey(userId, key), fallback);
}

export function scopedLsSet<T>(userId: string, key: string, value: T): void {
  lsSet(accountKey(userId, key), value);
}

export function scopedLsRemove(userId: string, key: string): void {
  lsRemove(accountKey(userId, key));
}

export function scopedIdbKey(userId: string, key: string): string {
  return accountKey(userId, key);
}

export function scopedIdbGet<T>(userId: string, key: string): Promise<T | null> {
  return idbGet<T>(accountKey(userId, key));
}

export function scopedIdbSet<T>(userId: string, key: string, value: T, ttlMs: number | null = null): Promise<boolean> {
  return idbSet(accountKey(userId, key), value, ttlMs);
}

export function scopedIdbDelete(userId: string, key: string): Promise<void> {
  return idbDelete(accountKey(userId, key));
}

export interface StoredDraftOverride {
  player_id: string;
  pick_no: number;
  draft_slot: number;
  roster_id: number | null;
}

export function loadDraftOverrides(draftId: string): StoredDraftOverride[] {
  return lsGet<StoredDraftOverride[]>(`${DRAFT_OVERRIDES_KEY}:${draftId}`, []);
}

export function saveDraftOverrides(draftId: string, overrides: StoredDraftOverride[]): void {
  if (overrides.length) lsSet(`${DRAFT_OVERRIDES_KEY}:${draftId}`, overrides);
  else lsRemove(`${DRAFT_OVERRIDES_KEY}:${draftId}`);
}

export function loadScopedDraftOverrides(userId: string, draftId: string): StoredDraftOverride[] {
  return scopedLsGet<StoredDraftOverride[]>(userId, `${DRAFT_OVERRIDES_KEY}:${draftId}`, []);
}

export function saveScopedDraftOverrides(userId: string, draftId: string, overrides: StoredDraftOverride[]): void {
  if (overrides.length) scopedLsSet(userId, `${DRAFT_OVERRIDES_KEY}:${draftId}`, overrides);
  else scopedLsRemove(userId, `${DRAFT_OVERRIDES_KEY}:${draftId}`);
}

/** Remove only the local material belonging to one signed-in account. */
export async function clearAccountCache(userId: string): Promise<void> {
  const db = await openDb();
  if (db) {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const request = tx.objectStore(STORE).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        if (String(cursor.key).startsWith(accountKey(userId, ''))) cursor.delete();
        cursor.continue();
      };
      await new Promise<void>((resolve) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      });
    } catch {
      // The signed-out session remains safe; a stale cache is unusable without
      // the next account session and will be overwritten on a later sign-in.
    }
  }

  if (typeof localStorage !== 'undefined') {
    const prefix = LS_PREFIX + accountKey(userId, '');
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(prefix)) localStorage.removeItem(key);
    }
  }
}

/** FR-64: "a user clearing site data resets the tool completely." */
export async function clearAll(): Promise<void> {
  const db = await openDb();
  if (db) {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
    } catch {
      // Ignore — localStorage is still cleared below.
    }
  }
  if (typeof localStorage !== 'undefined') {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(LS_PREFIX)) localStorage.removeItem(key);
    }
  }
}

function isExpired(envelope: CacheEnvelope<unknown>): boolean {
  if (envelope.ttl_ms === null) return false;
  return Date.now() - envelope.stored_at > envelope.ttl_ms;
}

// ---------------------------------------------------------------------------
// localStorage — small config
// ---------------------------------------------------------------------------

export function lsGet<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function lsSet<T>(key: string, value: T): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded or private mode. Config is nice to have; losing it must
    // never interrupt a draft.
  }
}

export function lsRemove(key: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LS_PREFIX + key);
  } catch {
    // See lsSet.
  }
}
